/**
 * Human-readable labels for CAPA enum values.
 *
 * QA #89: the CAPA list and detail pages rendered raw enum values under a CSS
 * `capitalize` class, so `risk_assessment` displayed as "Risk_assessment".
 * CSS cannot fix this — `capitalize` only uppercases the first letter of each
 * word and has no idea `_` is a word separator.
 *
 * QA #87c: the same `capitalize` treatment rendered capa_type `both` as "Both",
 * which QA reported as unclear. `both` is an accurate stored value (a CAPA
 * created from a HIRA hazard can carry a corrective AND a preventive action),
 * so the value is kept and only the label is made explicit.
 *
 * Both pages import from here so the two views cannot drift apart — that drift
 * is itself the defect class behind QA #87.
 */

// backend/db/models/tenant/capa.py :: CAPASource
export const CAPA_SOURCE_LABELS = {
  incident: 'Incident',
  audit: 'Audit',
  customer_complaint: 'Customer Complaint',
  risk_assessment: 'Risk Assessment',
  inspection: 'Inspection',
  management_review: 'Management Review',
  regulatory: 'Regulatory',
  other: 'Other',
};

// backend/db/models/tenant/capa.py :: CAPAType.
// NOTE `immediate` is offered by the standalone create form but is NOT a member
// of the CAPAType enum, so it is stored unvalidated. Labelled here so it does
// not render raw; the underlying data-model gap is tracked separately.
export const CAPA_TYPE_LABELS = {
  corrective: 'Corrective',
  preventive: 'Preventive',
  both: 'Corrective & Preventive',
  immediate: 'Immediate',
};

/** Title-case an unknown snake_case value rather than showing it raw. */
const humanizeSlug = (value) =>
  String(value)
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const labelFor = (map, value, fallback) => {
  if (value === null || value === undefined || value === '') return fallback;
  return map[value] || humanizeSlug(value);
};

export const capaSourceLabel = (value) => labelFor(CAPA_SOURCE_LABELS, value, '—');
export const capaTypeLabel = (value) => labelFor(CAPA_TYPE_LABELS, value, '—');

/**
 * Display label for a CAPA assignee.
 *
 * QA #87d: before the assignee picker landed, the standalone create form posted
 * the literal sentinel 'temp-user-id' as `assigned_to` alongside a free-text
 * name, so historical rows carry a name that is not backed by any real user.
 * Those rows are NOT rewritten (mutating the assignee on a compliance record to
 * fix a display is the wrong trade, and reassignment is the customer's call) —
 * they are shown as Unassigned so the UI stops implying an accountable owner
 * that the system cannot actually notify.
 */
export const TEMP_ASSIGNEE_SENTINEL = 'temp-user-id';

export const capaAssigneeLabel = (capa) => {
  if (!capa) return 'Unassigned';
  const id = capa.assigned_to;
  if (!id || id === TEMP_ASSIGNEE_SENTINEL) return 'Unassigned';
  return capa.assigned_to_name || 'Unassigned';
};
